import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNav from '../components/AppNav';
import config from '../config';
import '../styles/HomePage.css';
import '../styles/ExplorePrayersPage.css';

const API = config.API_URL || '/api';

const APP_STORE_URL  = 'https://apps.apple.com/us/app/yesod/id6748472269';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=us.yesod.app';

const CATEGORIES = [
  { id: 'all',      label: 'All',       emoji: '🙏' },
  { id: 'soldiers', label: 'Soldiers',  emoji: '🪖' },
  { id: 'israel',   label: 'Israel',    emoji: '🇮🇱' },
  { id: 'health',   label: 'Refuah',    emoji: '❤️‍🩹' },
  { id: 'family',   label: 'Families',  emoji: '👨‍👩‍👧' },
  { id: 'children', label: 'Children',  emoji: '🌱' },
  { id: 'shidduch', label: 'Shidduch',  emoji: '💍' },
  { id: 'memory',   label: 'Memory',    emoji: '🕯️' },
];

// ─── ExploreCard ────────────────────────────────────────────────────────────
const ExploreCard = ({ pg }) => {
  const goal     = pg.book_completion_goal || 150;
  const chapters = pg.chapters_in_current_book || 0;
  const pct      = Math.min(100, (chapters / goal) * 100);
  const title    = pg.group_name || pg.recipient_name || 'Prayer Circle';
  const sub      = pg.intention_display_text || 'Tehillim';

  const handleClick = () => window.open(`/${pg.short_code}/details`, '_blank');
  const handleKey   = (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); };

  return (
    <article
      className="ep-card"
      onClick={handleClick}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
      aria-label={`Pray for ${title}`}
    >
      <div className="ep-card__band">
        {pg.card_image_url
          ? <img src={pg.card_image_url} alt="" loading="lazy" />
          : null}
        <div className="ep-card__band-scrim" />
        <span className="ep-card__pill">Tehillim</span>
      </div>

      <div className="ep-card__body">
        <h3 className="ep-card__title">{title}</h3>
        <p className="ep-card__sub">{sub}</p>

        <div className="ep-card__progress">
          <div className="ep-card__track">
            <div className="ep-card__fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="ep-card__pct">{chapters} of {goal} chapters</span>
        </div>

        <div className="ep-card__join-btn">Join Group →</div>
      </div>
    </article>
  );
};

// ─── SkeletonCard ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="ep-card ep-card--skel" aria-hidden="true">
    <div className="ep-card__band" />
    <div className="ep-card__body">
      <div className="ep-skel-block" style={{ height: 15, width: '68%', marginBottom: 8 }} />
      <div className="ep-skel-block" style={{ height: 11, width: '48%', marginBottom: 18 }} />
      <div className="ep-skel-block" style={{ height: 5, width: '100%', borderRadius: 999, marginBottom: 6 }} />
      <div className="ep-skel-block" style={{ height: 10, width: '42%', marginBottom: 18 }} />
      <div className="ep-skel-block" style={{ height: 36, borderRadius: 10 }} />
    </div>
  </div>
);

// ─── ExplorePrayersPage ──────────────────────────────────────────────────────
export default function ExplorePrayersPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [groups, setGroups]     = useState([]);
  const [loading, setLoading]   = useState(true);
  // On desktop show 2 rows (8 cards at 4-col); on mobile show all
  const [visibleCount, setVisibleCount] = useState(
    () => (typeof window !== 'undefined' && window.innerWidth < 768) ? null : 8
  );
  const pillsRef = useRef(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleAppDownload = () => {
    const url = isMobile
      ? (/iPad|iPhone|iPod/.test(navigator.userAgent) ? APP_STORE_URL : PLAY_STORE_URL)
      : APP_STORE_URL;
    window.open(url, '_blank');
  };

  const fetchGroups = useCallback((category) => {
    setLoading(true);
    fetch(`${API}/prayer-groups/public/explore?category=${encodeURIComponent(category)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setGroups(d?.data || []);
        // Reset to 2-row view on desktop when new category data arrives
        if (typeof window !== 'undefined' && window.innerWidth >= 768) setVisibleCount(8);
      })
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchGroups(activeCategory); }, [activeCategory, fetchGroups]);

  const handleCategoryChange = (id) => {
    setActiveCategory(id);
    const pill = pillsRef.current?.querySelector(`[data-cat="${id}"]`);
    pill?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  return (
    <div className="ep-root">

      {/* ── Nav ── */}
      <AppNav cta={{ label: '+ Start a prayer', onClick: handleAppDownload }} />

      {/* ── Hero ── */}
      <header className="ep-hero">
        <div className="ep-hero__eyebrow">Chessed</div>
        <h1 className="ep-hero__title">
          Every prayer<br />
          <span className="ep-hero__title-accent">makes a difference</span>
        </h1>
        <p className="ep-hero__sub">
          Join thousands of people saying Tehillim for those who need it most.<br />
          Find someone to pray for — and make your prayers count.
        </p>
      </header>

      {/* ── Category pills ── */}
      <div className="ep-cats-wrap">
        <ul className="ep-cats" ref={pillsRef} role="list" aria-label="Prayer categories">
          {CATEGORIES.map(cat => (
            <li key={cat.id}>
              <button
                className={`ep-cat${activeCategory === cat.id ? ' ep-cat--active' : ''}`}
                data-cat={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                aria-pressed={activeCategory === cat.id}
              >
                <span className="ep-cat__emoji" aria-hidden="true">{cat.emoji}</span>
                {cat.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Grid ── */}
      <main className="ep-grid-wrap">
        <div className="ep-grid">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            : groups.length === 0
              ? (
                <div className="ep-empty">
                  <span className="ep-empty__icon">🙏</span>
                  <p className="ep-empty__text">No active prayer chains in this category yet.</p>
                  <button className="ep-empty__cta" onClick={handleAppDownload}>
                    Start one for someone you love
                  </button>
                </div>
              )
              : (visibleCount != null ? groups.slice(0, visibleCount) : groups)
                  .map(pg => <ExploreCard key={pg.id} pg={pg} />)
          }
        </div>
        {!loading && visibleCount != null && groups.length > visibleCount && (
          <div className="ep-show-more">
            <button
              className="ep-show-more__btn"
              onClick={() => setVisibleCount(null)}
            >
              Show {groups.length - visibleCount} more prayer circles
            </button>
          </div>
        )}
      </main>

      {/* ── App download CTA ── */}
      <section className="ep-app" aria-labelledby="ep-app-title">
        <div className="ep-app__inner">
          <div className="ep-app__left">
            <p className="ep-app__eyebrow">Take it everywhere</p>
            <h2 className="ep-app__title" id="ep-app-title">
              Continue praying<br />on the go
            </h2>
            <p className="ep-app__sub">
              Track chapters, join circles, and pray alongside your community
              — all from your phone.
            </p>
            <div className="ep-app__btns">
              {isMobile ? (
                <button className="ep-app__btn ep-app__btn--ios" onClick={handleAppDownload}>
                  <AppleIcon />
                  Download the app
                </button>
              ) : (
                <>
                  <a href={APP_STORE_URL} target="_blank" rel="noreferrer"
                    className="ep-app__btn ep-app__btn--ios">
                    <AppleIcon />
                    App Store
                  </a>
                  <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer"
                    className="ep-app__btn ep-app__btn--android">
                    <AndroidIcon />
                    Google Play
                  </a>
                </>
              )}
            </div>
          </div>
          <div className="ep-app__right" aria-hidden="true">
            <div className="ep-app__phone">📖</div>
          </div>
        </div>
      </section>

    </div>
  );
}

// ─── Icon helpers ────────────────────────────────────────────────────────────
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const AndroidIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M6.18 15.64a2.18 2.18 0 0 1-2.18-2.18V9.76a2.18 2.18 0 1 1 4.36 0v3.7a2.18 2.18 0 0 1-2.18 2.18zm11.64 0a2.18 2.18 0 0 1-2.18-2.18V9.76a2.18 2.18 0 1 1 4.36 0v3.7a2.18 2.18 0 0 1-2.18 2.18zM12 2a7 7 0 0 0-7 7v7.5a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 16.5V9a7 7 0 0 0-7-7zm-2 18.5a1.5 1.5 0 0 0 3 0V20h-3v.5zm-1.5-13a.5.5 0 1 1 1 0 .5.5 0 0 1-1 0zm5 0a.5.5 0 1 1 1 0 .5.5 0 0 1-1 0zm-6.8-3.3 1.1-1.9a.3.3 0 0 1 .52.3l-1.1 1.9a.3.3 0 0 1-.52-.3zm9.6 0a.3.3 0 0 1-.52.3l-1.1-1.9a.3.3 0 0 1 .52-.3l1.1 1.9z" />
  </svg>
);
